/**
 * amenity-icons — the property slice's domain MAPPING from amenity category to
 * a kit `IconName` (SCRUM-141 / DS-010-5).
 *
 * Doctrine (plan §"Icon registry doctrine"): the kit owns the glyph INVENTORY
 * (`@sparta/ui-kit` REGISTRY); this slice owns the category → glyph MAPPING plus
 * a generic fallback. `IconName` is imported from the kit so a glyph rename in
 * the registry breaks this map at typecheck (the map cannot drift silently).
 *
 * `CATEGORY_ICON` is typed `Record<AmenityCategory, IconName>` — TypeScript
 * therefore guarantees exactly one entry per known category (no missing key,
 * no phantom key), which the spec asserts at runtime as a second guard.
 *
 * `resolveCategoryIcon` accepts a raw `string` (not `AmenityCategory`) on
 * purpose: the catalog response drives grouping, so a NEW or unknown BE
 * category must render with the fallback glyph rather than crash. Prototype
 * keys (`__proto__`), wrong case (`Basics`), and trailing separators
 * (`basics-`) all miss the map and fall back.
 */

import type { IconName } from '@sparta/ui-kit';
import {
  AMENITY_CATEGORY_VALUES,
  type AmenityCategory,
} from '@artemis/validators/policies';

/**
 * One glyph per known amenity category. The `Record<AmenityCategory, IconName>`
 * type makes both the exhaustiveness (all 13) and the validity (every value is
 * a real `IconName`) compile-time facts.
 */
export const CATEGORY_ICON: Record<AmenityCategory, IconName> = {
  basics: 'sparkles',
  bathroom: 'bath',
  'bedroom-and-laundry': 'bed',
  entertainment: 'tv',
  family: 'baby',
  'heating-and-cooling': 'thermometer',
  'home-safety': 'shield',
  'internet-and-office': 'wifi',
  'kitchen-and-dining': 'utensils',
  location: 'mapPin',
  outdoor: 'trees',
  'parking-and-facilities': 'car',
  accessibility: 'accessibility',
};

/**
 * The generic fallback glyph for an unmapped / future BE category — an
 * unknown category renders this rather than crashing the picker.
 */
export const FALLBACK_CATEGORY_ICON: IconName = 'sparkles';

/**
 * The set of known category keys, materialised once as a `Set` for O(1)
 * membership checks. Derived from the validators' source-of-truth list so a
 * taxonomy change flows through without touching this file's lookup logic.
 */
const KNOWN_CATEGORIES: ReadonlySet<string> = new Set(AMENITY_CATEGORY_VALUES);

/**
 * Resolve a raw category string to its kit glyph. Known categories map via
 * `CATEGORY_ICON`; everything else (empty, wrong-case, prototype keys, unknown
 * future categories) returns `FALLBACK_CATEGORY_ICON`.
 *
 * The explicit `KNOWN_CATEGORIES.has` guard (rather than a bare index +
 * nullish-coalesce) hardens against prototype-chain keys such as `__proto__`,
 * `constructor`, or `toString` that a plain object index could otherwise
 * resolve to a truthy non-`IconName` value.
 */
export function resolveCategoryIcon(category: string): IconName {
  if (KNOWN_CATEGORIES.has(category)) {
    return CATEGORY_ICON[category as AmenityCategory];
  }
  return FALLBACK_CATEGORY_ICON;
}
